import { useMemo, useCallback, useState } from 'react';
import { useQuery, gql } from '@apollo/client';
import {
  FeatureFlag,
  QueryFindManyFeatureFlagArgs,
  FeatureFlagState,
  FeatureFlagType,
  useAddClinicFeatureFlagMutation,
  useRemoveClinicFeatureFlagMutation,
} from 'shared/types/graphql';
import useClinicUser from 'shared/hooks/useClinicUser';
import { FeatureFlagName } from 'shared/enums';

const GET_CLINIC_FEATURE_FLAGS = gql`
  query getFeatureFlags($where: FeatureFlagWhereInput!) {
    featureFlags: findManyFeatureFlag(where: $where) {
      id
      key
      type
    }
  }
`;

interface IUseFeatureFlagResult {
  isFeatureEnabled: (key: string) => boolean;
  addFeatureFlag: (key: FeatureFlagName) => Promise<void>;
  removeFeatureFlag: (key: FeatureFlagName) => Promise<void>;
  isFeatureGlobal: (key: string) => boolean;
  featureFlags: Record<string, FeatureFlag>;
  isFeatureFlagsLoading: boolean;
}

const useFeatureFlag = (): IUseFeatureFlagResult => {
  const { currentClinicId } = useClinicUser();
  const [isLoading, setIsLoading] = useState(true);
  const [addClinicFeatureFlag] = useAddClinicFeatureFlagMutation();
  const [removeClinicFeatureFlag] = useRemoveClinicFeatureFlagMutation();

  const { data: featureFlagsData, refetch: refetchFeatureFlags } = useQuery<
    { featureFlags: FeatureFlag[] },
    QueryFindManyFeatureFlagArgs
  >(GET_CLINIC_FEATURE_FLAGS, {
    skip: !currentClinicId,
    variables: {
      where: {
        state: { not: { equals: FeatureFlagState.Off } },
        OR: [
          {
            type: { equals: FeatureFlagType.Clinic },
            clinics: { some: { id: { equals: currentClinicId } } },
          },
          {
            type: { equals: FeatureFlagType.Global },
          },
        ],
      },
    },
    onCompleted: () => setIsLoading(false),
  });

  /**
   * Transform to a Record<key, FeatureFlag> for easy access
   */
  const featureFlags = useMemo((): Record<string, FeatureFlag> => {
    const featureFlags: Record<string, FeatureFlag> = {};

    if (!featureFlagsData) return featureFlags;

    for (const featureFlag of featureFlagsData.featureFlags) {
      featureFlags[featureFlag.key] = featureFlag;
    }

    return featureFlags;
  }, [featureFlagsData]);

  /**
   * Check if feature is enabled by key
   */
  const isFeatureEnabled = useCallback(
    (key: string): boolean => {
      return !!featureFlags[key];
    },
    [featureFlags],
  );

  /**
   * Check if feature is global by key
   */
  const isFeatureGlobal = useCallback(
    (key: string): boolean => {
      return featureFlags[key]?.type === FeatureFlagType.Global;
    },
    [featureFlags],
  );

  const addFeatureFlag = useCallback(
    async (featureFlagKey: FeatureFlagName) => {
      if (isFeatureEnabled(featureFlagKey)) return;

      try {
        await addClinicFeatureFlag({
          variables: {
            clinicId: currentClinicId || '',
            featureFlagKey,
          },
        });
        refetchFeatureFlags();
      } catch (e) {
        console.error(`Error adding feature flag: ${featureFlagKey}`, e);
      }
    },
    [addClinicFeatureFlag, currentClinicId, isFeatureEnabled, refetchFeatureFlags],
  );

  const removeFeatureFlag = useCallback(
    async (featureFlagKey: FeatureFlagName) => {
      if (!isFeatureEnabled(featureFlagKey) || !currentClinicId) return;

      try {
        await removeClinicFeatureFlag({
          variables: {
            clinicId: currentClinicId || '',
            featureFlagKey,
          },
        });
        refetchFeatureFlags();
      } catch (e) {
        console.error(`Error removing feature flag: ${featureFlagKey}`, e);
      }
    },
    [currentClinicId, isFeatureEnabled, refetchFeatureFlags, removeClinicFeatureFlag],
  );

  return {
    isFeatureEnabled,
    addFeatureFlag,
    removeFeatureFlag,
    isFeatureGlobal,
    featureFlags,
    isFeatureFlagsLoading: isLoading,
  };
};

export default useFeatureFlag;
